import React, { useState, useEffect, useCallback } from 'react';
import { apiService } from '../services/api';

const UnifiedRankings = ({ onVideoClick }) => {
  const [rankings, setRankings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(new Date());

  const fetchRankings = useCallback(async () => {
    // Check cache first
    const cacheKey = 'rankings_cache';
    const cached = localStorage.getItem(cacheKey);
    const cacheTime = localStorage.getItem(`${cacheKey}_time`);
    const now = Date.now();
    
    // Use cache if less than 5 minutes old
    if (cached && cacheTime && (now - parseInt(cacheTime)) < 5 * 60 * 1000) {
      try {
        const cachedData = JSON.parse(cached);
        setRankings(cachedData.rankings || []);
        setLastUpdate(new Date(parseInt(cacheTime)));
        setIsLoading(false);
        return;
      } catch (e) {
        // Cache invalid, fetch fresh
      }
    }

    setIsLoading(true);
    setError(null);
    try {
      const data = await apiService.getCategoryRankings(null, 50);
      if (data.success) {
        setRankings(data.rankings || []);
        setLastUpdate(new Date());
        // Cache the results
        localStorage.setItem(cacheKey, JSON.stringify(data));
        localStorage.setItem(`${cacheKey}_time`, now.toString());
      }
    } catch (err) {
      setError(err.message || 'Failed to load rankings');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRankings();
  }, [fetchRankings]);

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num?.toString() || '0';
  };

  const formatDuration = (seconds) => {
    if (!seconds) return 'N/A';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getScoreColor = (score) => {
    if (score >= 4) return '#10b981';
    if (score >= 3) return '#f59e0b';
    return '#ef4444';
  };

  const getRankMedal = (index) => {
    if (index === 0) return '🥇';
    if (index === 1) return '🥈';
    if (index === 2) return '🥉';
    return `${index + 1}`;
  };

  // Generate floating particles
  const generateParticles = () => {
    const particles = [];
    const colors = ['#8b5cf6', '#ec4899', '#06b6d4', '#10b981'];
    
    for (let i = 0; i < 50; i++) {
      particles.push(
        <div
          key={i}
          className="particle"
          style={{
            width: Math.random() * 4 + 2 + 'px',
            height: Math.random() * 4 + 2 + 'px',
            background: `linear-gradient(45deg, ${colors[Math.floor(Math.random() * 4)]}, transparent)`,
            left: Math.random() * 100 + '%',
            top: Math.random() * 100 + '%',
            animation: `float ${Math.random() * 10 + 10}s ease-in-out infinite`,
            animationDelay: Math.random() * 5 + 's'
          }}
        />
      );
    }
    return particles;
  };

  return (
    <div className="page-container">
      {/* Animated Background Elements */}
      <div className="animated-background" />

      {/* Floating particles */}
      <div className="floating-particles">
        {generateParticles()}
      </div>

      <div className="page-content">
        {/* Hero Section */}
        <div className="hero-section">
          <h1 className="hero-title">
            Rankings & Leaderboard
          </h1>
          <p className="hero-subtitle">
            Top videos ranked across all exam verticals
          </p>
          <p style={{
            fontSize: '0.85rem',
            color: '#9ca3af',
            marginTop: '-1rem'
          }}>
            Last updated: {lastUpdate.toLocaleTimeString()}
          </p>
        </div>

        {error && (
          <div className="error-container">
            <p className="error-content">{error}</p>
          </div>
        )}

        {isLoading ? (
          <div className="results-container" style={{ textAlign: 'center', padding: '3rem' }}>
            <div className="spinner" style={{ margin: '0 auto', width: '40px', height: '40px', borderWidth: '3px' }} />
            <p style={{ marginTop: '1rem', color: '#9ca3af' }}>Loading rankings...</p>
          </div>
        ) : rankings.length > 0 ? (
          <div className="results-container">
            <div style={{
              display: 'grid',
              gap: '1.5rem'
            }}>
              {rankings.map((video, index) => (
                <div
                  key={video.video_id}
                  className="step-card"
                  style={{
                    display: 'flex',
                    border: index < 3 
                      ? '2px solid rgba(139, 92, 246, 0.5)' 
                      : '2px solid rgba(139, 92, 246, 0.3)',
                    boxShadow: index < 3 
                      ? '0 8px 32px rgba(139, 92, 246, 0.3)' 
                      : '0 8px 32px rgba(0, 0, 0, 0.3)',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = index < 3
                      ? '0 12px 40px rgba(139, 92, 246, 0.5)'
                      : '0 12px 40px rgba(139, 92, 246, 0.3)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = index < 3
                      ? '0 8px 32px rgba(139, 92, 246, 0.3)'
                      : '0 8px 32px rgba(0, 0, 0, 0.3)';
                  }}
                  onClick={() => {
                    if (onVideoClick) {
                      onVideoClick(video.video_id);
                    } else {
                      window.open(`https://www.youtube.com/watch?v=${video.video_id}`, '_blank');
                    }
                  }}
                >
                  {/* Rank Badge */}
                  <div style={{
                    background: index < 3 
                      ? 'linear-gradient(135deg, rgba(139, 92, 246, 0.4), rgba(236, 72, 153, 0.3))' 
                      : 'linear-gradient(135deg, rgba(139, 92, 246, 0.3), rgba(236, 72, 153, 0.2))',
                    color: '#e5e7eb',
                    padding: '1.5rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '2rem',
                    fontWeight: 'bold',
                    minWidth: '80px',
                    flexDirection: 'column',
                    gap: '0.5rem'
                  }}>
                    <div style={{ fontSize: '2rem' }}>{getRankMedal(index)}</div>
                  </div>

                  {/* Thumbnail */}
                  <div style={{
                    width: '280px',
                    height: '158px',
                    flexShrink: 0,
                    position: 'relative',
                    background: '#000'
                  }}>
                    <img
                      src={video.thumbnail}
                      alt={video.title}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                    />
                    {video.duration > 0 && (
                      <div style={{
                        position: 'absolute',
                        bottom: '8px',
                        right: '8px',
                        background: 'rgba(0,0,0,0.85)',
                        color: 'white',
                        padding: '4px 8px',
                        borderRadius: '6px',
                        fontSize: '0.85rem',
                        fontWeight: 'bold'
                      }}>
                        {formatDuration(video.duration)}
                      </div>
                    )}
                  </div>

                  {/* Video Info */}
                  <div style={{
                    flex: 1,
                    padding: '1.5rem',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    minWidth: 0
                  }}>
                    <div>
                      <h3 style={{
                        fontSize: '1.1rem',
                        fontWeight: '700',
                        color: '#e5e7eb',
                        marginBottom: '0.5rem',
                        lineHeight: '1.4',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical'
                      }}>
                        {video.title}
                      </h3>
                      <p style={{
                        fontSize: '0.9rem',
                        color: '#9ca3af',
                        marginBottom: '0.5rem'
                      }}>
                        {video.channel}
                      </p>
                      {video.summary && (
                        <p style={{
                          fontSize: '0.85rem',
                          color: '#6b7280',
                          lineHeight: '1.5',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical'
                        }}>
                          {video.summary}
                        </p>
                      )}
                    </div>

                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginTop: '1rem',
                      gap: '0.5rem',
                      flexWrap: 'wrap'
                    }}>
                      {video.category && (
                        <span style={{
                          background: 'rgba(139, 92, 246, 0.2)',
                          border: '1px solid rgba(139, 92, 246, 0.4)',
                          color: '#a78bfa',
                          padding: '4px 10px',
                          borderRadius: '8px',
                          fontSize: '0.75rem',
                          fontWeight: '600'
                        }}>
                          {video.category}
                        </span>
                      )}
                      <span style={{
                        background: video.language === 'hinglish' 
                          ? 'rgba(139, 92, 246, 0.2)' 
                          : 'rgba(236, 72, 153, 0.2)',
                        border: `1px solid ${video.language === 'hinglish' ? 'rgba(139, 92, 246, 0.4)' : 'rgba(236, 72, 153, 0.4)'}`,
                        color: video.language === 'hinglish' ? '#a78bfa' : '#f9a8d4',
                        padding: '4px 10px',
                        borderRadius: '8px',
                        fontSize: '0.75rem',
                        fontWeight: '600'
                      }}>
                        {video.language === 'hinglish' ? '🇮🇳 HI' : '🇬🇧 EN'}
                      </span>
                    </div>
                  </div>

                  {/* Gradi Score */}
                  <div style={{
                    background: `linear-gradient(135deg, ${getScoreColor(video.gradi_score)}15, ${getScoreColor(video.gradi_score)}05)`,
                    border: `2px solid ${getScoreColor(video.gradi_score)}40`,
                    color: 'white',
                    padding: '1.5rem',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: '110px'
                  }}>
                    <div style={{
                      fontSize: '2rem',
                      fontWeight: '900',
                      lineHeight: '1',
                      color: getScoreColor(video.gradi_score),
                      textShadow: `0 0 10px ${getScoreColor(video.gradi_score)}40`
                    }}>
                      {video.gradi_score.toFixed(1)}
                    </div>
                    <div style={{
                      fontSize: '0.75rem',
                      marginTop: '0.5rem',
                      color: '#9ca3af',
                      fontWeight: '600'
                    }}>
                      / 5.0
                    </div>
                    <div style={{
                      fontSize: '0.7rem',
                      marginTop: '0.5rem',
                      color: '#6b7280',
                      textAlign: 'center'
                    }}>
                      Gradi Score
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="results-container" style={{
            textAlign: 'center',
            padding: '3rem',
            color: '#9ca3af'
          }}>
            No videos found yet. Try analyzing a video to populate the rankings.
          </div>
        )}
      </div>
    </div>
  );
};

export default UnifiedRankings;

